import videojs from "video.js/core.es.js";
import type ButtonClass from "video.js/dist/types/button";
import type Component from "video.js/dist/types/component";
import type Player from "video.js/dist/types/player";

const Button = videojs.getComponent("Button") as typeof ButtonClass;

export interface ChainNavButtonOptions {
  navOnClick?: () => void;
  controlText?: string;
  buttonClass?: string;
  iconSvg?: string;
}

/**
 * ChainNavButton — a control-bar button for stepping the chain
 * (previous / next video). Thin over vjs Button: it shows a skip icon, calls the
 * `navOnClick` callback from options on click, and exposes setEnabled() so the
 * owner (VideoJs) can disable it at the ends of the chain. All the navigation
 * logic lives in VideoJs + chainNav; this is just the control.
 */
class ChainNavButton extends Button {
  declare _navOnClick: (() => void) | undefined;

  constructor(player: Player, options: ChainNavButtonOptions) {
    super(player, options);
    this._navOnClick = options.navOnClick;
    if (options.controlText) {
      this.controlText(options.controlText);
    }
    if (options.buttonClass) {
      this.addClass(options.buttonClass);
    }
    // Inject the icon into vjs Button's icon placeholder so it themes with the
    // control bar (currentColor) and needs no icon-font.
    const placeholder = this.el().querySelector(".vjs-icon-placeholder");
    if (placeholder && options.iconSvg) {
      placeholder.innerHTML = options.iconSvg;
    }
  }

  handleClick(event: Event) {
    super.handleClick(event);
    // Disabled ClickableComponents still receive the DOM click in some paths;
    // guard so an end-of-chain arrow is inert.
    if (this.hasClass("vjs-disabled")) {
      return;
    }
    if (this._navOnClick) {
      this._navOnClick();
    }
  }

  setEnabled(enabled: boolean) {
    if (enabled) {
      this.enable();
    } else {
      this.disable();
    }
  }
}

videojs.registerComponent("ChainNavButton", ChainNavButton as typeof Component);

export default ChainNavButton;
